/**
 * alarmAcks — reconocimiento de alarmas por operador (ARS-17/ARS-18),
 * compartido entre todos los operadores vía /api/alarm-acks (auth-gateway).
 * Un reconocimiento vale para la ocurrencia actual de la alarma: el backend
 * lo deriva de events.db (último `alarm_ack` sin `alarm_clear` posterior),
 * así que al resolverse la alarma desaparece solo y una reaparición llega
 * sin reconocer. Mismo patrón de store que alarmMasks.ts.
 */
import { create } from 'zustand';

// Espejo de ACK_ROLES en tools/auth-gateway/server.py — solo decide si se
// muestra el botón; el backend es quien rechaza a un `viewer`.
export const ACK_ROLES = new Set(['operator', 'maintainer', 'org_admin', 'superadmin']);

export interface AlarmAck {
  canonicalId: string;
  code: string;
  ackedBy: string;
  ackedAt: string;
  note: string;
}

interface AlarmAcksState {
  acks: AlarmAck[];
  fetchAcks: () => Promise<void>;
  ack: (canonicalId: string, code: string, note: string) => Promise<boolean>;
}

export const useAlarmAcks = create<AlarmAcksState>((set) => ({
  acks: [],

  fetchAcks: async () => {
    try {
      const res = await fetch('/api/alarm-acks', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      set({ acks: data.acks ?? [] });
    } catch {
      // sin conexión momentánea: el polling vuelve a intentarlo
    }
  },

  ack: async (canonicalId, code, note) => {
    try {
      const res = await fetch('/api/alarm-acks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canonicalId, code, note }),
      });
      if (!res.ok) return false;
      const created: AlarmAck = await res.json();
      // Sustituye un ack previo de la misma alarma (otro operador) sin
      // esperar al siguiente fetch.
      set(s => ({
        acks: [...s.acks.filter(a => !(a.canonicalId === canonicalId && a.code === code)), created],
      }));
      return true;
    } catch {
      return false;
    }
  },
}));

export function findAck(acks: AlarmAck[], canonicalId: string, code: string): AlarmAck | undefined {
  return acks.find(a => a.canonicalId === canonicalId && a.code === code);
}
